import { Box, Chip, Paper, Portal, useTheme } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridToolbarQuickFilter,
  useGridApiRef,
} from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { useGetUserDetails } from "src/helpers/UserDetails";
import { generateUniqueKey } from "src/reusable/Functions";
import { whereFilterType } from "src/types/types";
import { searchDatasetFolders } from "src/views/datasets/services/DatasetServices";

function QuickSearchToolbar() {
  return (
    <Box sx={{ width: "100%" }}>
      <Paper elevation={0}>
        <Portal
          container={() => document.getElementById("dataset-search-bar")!}
        >
          <GridToolbarQuickFilter
            placeholder="Search for datasets"
            variant="outlined"
            size="small"
            sx={{
              width: "100%",
              paddingBottom: 0,
            }}
          />
        </Portal>
      </Paper>
    </Box>
  );
}

type QueryOptions = {
  searchQuery: string;
  filters?: whereFilterType[];
  folderId?: number;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDesc?: boolean;
  searchAllFolders?: boolean;
  starredOnly?: boolean;
};

type QueryKey = string | (string | number | boolean | undefined)[];

type DataGridSearchProps = {
  columns: readonly GridColDef<any>[];
  datasetFolderColumns?: readonly GridColDef<any>[];
  queryFn: (query: any) => Promise<any>;
  queryKey?: QueryKey;
  onRowClick?: (row: any) => void;
  filterComponent?: React.JSXElementConstructor<any>;
  columnVisibilityModel?: any;
  noRowsOverlay?: React.JSXElementConstructor<any>;
  atomResults?: boolean;
};

const DataGridSearch = ({
  columns,
  datasetFolderColumns,
  queryKey: initialQueryKey,
  queryFn,
  onRowClick,
  filterComponent: FilterComponent,
  columnVisibilityModel,
  noRowsOverlay,
  atomResults = false,
}: DataGridSearchProps) => {
  const { folder_id }: any = useParams();
  const [queryKey, setQueryKey] = useState<QueryKey>(initialQueryKey || "");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [userFilters, setUserFilters] = useState<whereFilterType[]>([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [sortModel, setSortModel] = useState([
    { field: "create_date", sort: "desc" as const },
  ]);

  const theme = useTheme();
  const user = useGetUserDetails();
  const userName = user?.user_display_name;

  const whereFilters = useMemo(() => {
    const allExceptAtomResults: whereFilterType = {
      filter_on: {
        group: "dataset",
        name: "dataset_type.name",
        column: "dataset_type.name",
      },
      comparison_operator: "ne",
      value_datatype: "string",
      filter_value: "AtomResults",
    };

    const onlyAtomResults: whereFilterType[] = [
      {
        filter_on: {
          group: "dataset",
          name: "dataset_type.name",
          column: "dataset_type.name",
        },
        comparison_operator: "eq",
        value_datatype: "string",
        filter_value: "AtomResults",
      },
      {
        filter_on: {
          group: "user",
          name: "create_user.user_display_name",
          column: "create_user.user_display_name",
        },
        comparison_operator: "eq",
        value_datatype: "string",
        filter_value: userName ?? "",
      },
    ];

    return atomResults
      ? [...onlyAtomResults, ...userFilters]
      : [allExceptAtomResults, ...userFilters];
  }, [userFilters, atomResults]);

  const queryOptions = useMemo(() => {
    const options: QueryOptions = {
      searchQuery,
      page: paginationModel.page + 1, // Convert 0-based to 1-based
      pageSize: paginationModel.pageSize,
      sortBy: sortModel[0]?.field || "create_date",
      sortDesc: sortModel[0]?.sort === "desc",
      starredOnly: showFavoritesOnly,
    };

    if (whereFilters) {
      options.filters = whereFilters;
    }

    return options;
  }, [
    searchQuery,
    whereFilters,
    paginationModel,
    sortModel,
    showFavoritesOnly,
  ]);

  const autoSizeOptions = {
    includeOutliers: true,
    expand: true,
  };

  const apiRef = useGridApiRef();
  // add the folder_id to the queryOptions as own property
  queryOptions.folderId = folder_id;

  // Only expand search scope to folder-nested datasets when the user is
  // actively filtering at the root level. Idle root view stays scoped to
  // root datasets so folder contents don't appear twice (issue #1727).
  const searchAllFolders =
    !folder_id &&
    (searchQuery.trim().length > 0 ||
      showFavoritesOnly ||
      userFilters.length > 0);
  queryOptions.searchAllFolders = searchAllFolders;

  const { data, isFetching, isSuccess } = useQuery({
    queryKey: [
      queryKey,
      searchQuery,
      whereFilters,
      paginationModel,
      sortModel,
      folder_id,
      atomResults,
      showFavoritesOnly,
      searchAllFolders,
    ],
    queryFn: () => queryFn(queryOptions),
    keepPreviousData: !atomResults,
    staleTime: 10 * 1000, // 10 seconds for search results
    cacheTime: 2 * 60 * 1000, // 2 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: 2,
  });
  const { data: datasetFolderData } = useQuery({
    queryKey: ["datasetFolders", queryOptions.searchQuery],
    queryFn: () =>
      searchDatasetFolders({
        searchQuery: queryOptions.searchQuery,
        page: 1,
        pageSize: 50,
        sortBy: "create_date",
        sortDesc: true,
      }),
    enabled: !Boolean(folder_id),
    staleTime: 30 * 1000, // 30 seconds for folder searches
    cacheTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false,
    retry: 2,
  });

  useEffect(() => {
    if (!initialQueryKey) {
      setQueryKey(generateUniqueKey());
    }
  }, [initialQueryKey]);

  useEffect(() => {
    if (isSuccess && apiRef.current) {
      setTimeout(() => {
        if (apiRef.current) {
          apiRef.current.autosizeColumns({
            includeOutliers: true,
            expand: true,
          });
        }
      }, 100);
    }
  }, [isSuccess, apiRef, columns]);

  // update api ref when viewport size changes
  useEffect(() => {
    const handleResize = () => {
      if (apiRef.current) {
        apiRef.current.autosizeColumns({
          columns: columns?.map((column) => column.field),
          includeOutliers: true,
          expand: true,
        });
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [apiRef, columns]);

  const handleSearchChange = useCallback(
    (newFilterModel: GridFilterModel) => {
      if (newFilterModel.quickFilterValues) {
        const newValues = newFilterModel.quickFilterValues.join(" ").trim();
        setSearchQuery(newValues);
        // Reset to first page when search changes
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
    },
    [setSearchQuery],
  );

  const handlePaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      setPaginationModel(newModel);
    },
    [],
  );

  const handleSortModelChange = useCallback((newModel: any) => {
    if (newModel.length > 0) {
      setSortModel(newModel);
    }
  }, []);

  // Combine and sort data to show folders first
  const combinedData = useMemo(() => {
    if (folder_id) {
      // If we're in a folder, only show datasets
      return data?.results ?? [];
    }

    const folders = datasetFolderData?.results ?? [];
    const datasets = data?.results ?? [];
    // add isFolder property to folders
    folders.forEach((folder: any) => {
      folder.isFolder = true;
    });

    if (atomResults) {
      return [...datasets];
    }

    // Return combined array with folders first
    return [...folders, ...datasets];
  }, [data, datasetFolderData, folder_id]);

  // Calculate total row count for pagination
  const totalRowCount = useMemo(() => {
    if (folder_id) {
      return data?.total_items ?? 0;
    }

    const folderCount = datasetFolderData?.results?.length ?? 0;
    const datasetCount = data?.total_items ?? 0;

    if (atomResults) {
      return datasetCount;
    }

    // For the combined view, we need to handle pagination differently
    // since folders are not paginated
    return folderCount + datasetCount;
  }, [data, datasetFolderData, folder_id, atomResults]);

  // Function to check if a row is a folder
  const isRowFolder = useCallback(
    (row: any) => {
      return row.hasOwnProperty("isFolder");
    },
    [datasetFolderData],
  );

  // Process data to ensure all rows have the necessary fields
  const processedData = useMemo(() => {
    return combinedData.map((row: any) => {
      // For folder rows, ensure they have the necessary fields
      if (isRowFolder(row)) {
        return {
          ...row,
          id: row.id, // Make folder IDs unique
          gridId: row.id + "-folder", // Add unique grid ID
          create_user:
            row.create_user && typeof row.create_user === "object"
              ? row.create_user.user_display_name || row.create_user.username
              : row.create_user,
          isFolder: true,
        };
      }
      // For dataset rows, ensure they have the necessary fields
      return {
        ...row,
        id: row.id, // Make dataset IDs unique
        gridId: row.id + "-dataset", // Add unique grid ID
        create_user:
          typeof row.create_user === "object"
            ? row.create_user.user_display_name || row.create_user.username
            : row.create_user,
      };
    });
  }, [combinedData, isRowFolder]);

  // Create unified columns that handle both datasets and folders
  const unifiedColumns = useMemo(() => {
    // Start with the base columns that are common to both
    const baseColumns = columns.filter(
      (col) =>
        // Strip the parent's visibility_level column (and legacy
        // allow_others_to_edit) so it isn't rendered twice: we re-append a
        // single folder-aware version below.
        ![
          "actions",
          "more-actions",
          "allow_others_to_edit",
          "visibility_level",
        ].includes(col.field),
    );

    // Add the conditional columns
    const dynamicColumns: GridColDef<any>[] = [
      ...baseColumns,
      // Visibility column - public/private (replaces the legacy "can others
      // edit"; access is now visibility + per-object grants, not an edit flag).
      {
        field: "visibility_level",
        headerName: "Visibility",
        flex: 1,
        renderCell: (params: any) => {
          if (params.row?.isFolder) {
            return "-";
          }
          const isPrivate = params.row?.visibility_level === "private";
          return (
            <Chip
              size="small"
              variant="outlined"
              label={isPrivate ? "Private" : "Public"}
              color={isPrivate ? "default" : "success"}
            />
          );
        },
      },
      // Actions column - render for both datasets and folders
      {
        field: "actions",
        type: "actions",
        cellClassName: (params: any) =>
          params.row?.isFolder ? "folder-row-actions" : "dataset-row-actions",
        width: 100,
        align: "right",
        getActions: (params: any) => {
          // For folders, return folder-specific actions
          if (params.row?.isFolder && datasetFolderColumns) {
            // We need to get the folder-specific actions from datasetFolderColumns
            // Since folders don't have an actions column defined, we'll create one with Chat
            const folderActionsCol = datasetFolderColumns.find(
              (col) => col.field === "actions",
            );
            if (folderActionsCol && "getActions" in folderActionsCol) {
              return folderActionsCol.getActions?.(params) || [];
            }
            // If no actions column defined for folders, return empty
            return [];
          }
          // Get the actions from the regular columns definition for datasets
          const actionsCol = columns.find((col) => col.field === "actions");
          if (actionsCol && "getActions" in actionsCol) {
            return actionsCol.getActions?.(params) || [];
          }
          return [];
        },
      },
      // More actions column - different logic for datasets vs folders
      {
        field: "more-actions",
        type: "actions",
        getActions: (params: any) => {
          if (params.row?.isFolder && datasetFolderColumns) {
            // Use folder-specific actions
            const folderActionsCol = datasetFolderColumns.find(
              (col) => col.field === "more-actions",
            );
            if (folderActionsCol && "getActions" in folderActionsCol) {
              return folderActionsCol.getActions?.(params) || [];
            }
          } else {
            // Use dataset-specific actions
            const datasetActionsCol = columns.find(
              (col) => col.field === "more-actions",
            );
            if (datasetActionsCol && "getActions" in datasetActionsCol) {
              return datasetActionsCol.getActions?.(params) || [];
            }
          }
          return [];
        },
      },
    ];

    return dynamicColumns;
  }, [columns, datasetFolderColumns, theme]);

  return (
    <Box>
      <Paper elevation={0}>
        <Box id="dataset-search-bar" />
      </Paper>
      <Box
        display="flex"
        justifyContent="space-between"
        mt={1}
        mb={1}
        flexWrap="wrap"
        gap={1}
      >
        {FilterComponent && (
          <FilterComponent
            whereFilters={userFilters}
            setWhereFilters={setUserFilters}
            atomResults={atomResults}
            showFavoritesOnly={showFavoritesOnly}
            onToggleFavorites={(value: boolean) => {
              setShowFavoritesOnly(value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
          />
        )}
        <Box id="toggle-dataset-view" />
      </Box>
      <Paper elevation={0}>
        <DataGrid
          apiRef={apiRef}
          rows={processedData}
          columns={unifiedColumns}
          getRowClassName={(params) => {
            return isRowFolder(params.row) ? "folder-row" : "dataset-row";
          }}
          sx={{
            "&& .MuiDataGrid-row": {
              cursor: "pointer",
            },
            // Hide action items by default for datasets
            "& .dataset-row-actions .render-item": {
              visibility: "hidden",
            },
            "& .MuiDataGrid-row:hover .dataset-row-actions .render-item": {
              visibility: "visible",
            },
            // Hide action items by default for folders
            "& .folder-row-actions .render-item": {
              visibility: "hidden",
            },
            "& .MuiDataGrid-row:hover .folder-row-actions .render-item": {
              visibility: "visible",
            },
            "& .MuiTablePagination-selectLabel": {
              mb: 0,
            },
            "& .MuiTablePagination-displayedRows": {
              mb: 0,
            },
            "--DataGrid-overlayHeight": "300px",
          }}
          disableRowSelectionOnClick
          density="compact"
          loading={isFetching}
          slots={{
            toolbar: QuickSearchToolbar,
            noRowsOverlay: noRowsOverlay,
          }}
          slotProps={{
            noRowsOverlay: {
              isFolder: false, // custom prop
            } as any,
          }}
          onRowClick={onRowClick}
          filterMode="server"
          onFilterModelChange={handleSearchChange}
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          sortModel={sortModel}
          paginationMode="server"
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={totalRowCount}
          pageSizeOptions={[10, 25, 50, 100]}
          autosizeOnMount
          autosizeOptions={autoSizeOptions}
          columnVisibilityModel={columnVisibilityModel}
          autoHeight
          rowHeight={60}
          disableColumnMenu
          getRowId={(row) => row.gridId} // Use gridId for DataGrid's internal tracking
        />
      </Paper>
    </Box>
  );
};

export default DataGridSearch;
